import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];

const getVal = (x, ...keys) => {
  for (const k of keys) {
    if (x?.[k] !== undefined && x?.[k] !== null) return x[k];
  }
  return "";
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const styles = {
  page: { padding: 0, fontSize: 11 },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 700 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 28, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  stat: {
    borderRadius: 10,
    border: "1px solid #e9ecef",
    background: "#f8f9fa",
    padding: 9,
    height: "100%",
  },
  tableWrap: {
    overflowX: "auto",
    maxHeight: 430,
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0, minWidth: 1120 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    background: "#07111f",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: { fontSize: 10, padding: "4px 7px", borderRadius: 20 },
};

export default function BankReconciliationPage() {
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [filter, setFilter] = useState({
    accountId: "",
    fromDate: today(),
    toDate: today(),
    status: "false",
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const companyId = Number(localStorage.getItem("companyId") || 0);

  const bankAccounts = useMemo(() => {
    return accounts.filter((a) => {
      const name = String(
        getVal(a, "AccountName", "accountName"),
      ).toLowerCase();
      const code = String(getVal(a, "AccountCode", "accountCode"));
      const type = String(
        getVal(a, "AccountType", "accountType"),
      ).toLowerCase();

      return (
        name.includes("bank") ||
        name.includes("upi") ||
        code === "1200" ||
        type === "bank"
      );
    });
  }, [accounts]);

  const totals = useMemo(() => {
    const debit = rows.reduce(
      (s, r) => s + num(getVal(r, "Debit", "debit")),
      0,
    );
    const credit = rows.reduce(
      (s, r) => s + num(getVal(r, "Credit", "credit")),
      0,
    );
    const reconciled = rows.filter((r) =>
      Boolean(getVal(r, "IsReconciled", "isReconciled")),
    ).length;
    const pending = rows.length - reconciled;

    return {
      debit,
      credit,
      net: debit - credit,
      reconciled,
      pending,
    };
  }, [rows]);

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      setError("");

      const res = await client.get("/accounts");
      setAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to load bank accounts",
      );
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadEntries = async () => {
    if (!filter.accountId) return alert("Select bank account");
    if (!filter.fromDate || !filter.toDate) return alert("Select date range");

    try {
      setLoadingRows(true);
      setError("");

      const params = {
        accountId: Number(filter.accountId),
        fromDate: filter.fromDate,
        toDate: filter.toDate,
      };

      if (filter.status !== "all") {
        params.isReconciled = filter.status === "true";
      }

      if (companyId > 0) {
        params.companyId = companyId;
      }

      const res = await client.get("/finance/bank-reconciliation", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to load reconciliation entries",
      );
    } finally {
      setLoadingRows(false);
    }
  };

  const reconcile = async (row) => {
    const journalDetailId = getVal(row, "JournalDetailId", "journalDetailId");
    if (!journalDetailId) return alert("Journal detail id missing");

    const bankReferenceNo = prompt("Enter Bank Reference No / UTR", "");
    if (bankReferenceNo === null) return;

    try {
      setSaving(true);

      await client.post("/finance/bank-reconciliation/reconcile", {
        journalDetailId: Number(journalDetailId),
        reconciledDate: today(),
        bankReferenceNo: bankReferenceNo.trim(),
      });

      await loadEntries();
    } catch (e) {
      console.error(e);
      alert(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to reconcile entry",
      );
    } finally {
      setSaving(false);
    }
  };

  const unreconcile = async (row) => {
    const journalDetailId = getVal(row, "JournalDetailId", "journalDetailId");
    if (!journalDetailId) return alert("Journal detail id missing");

    if (!window.confirm("Undo reconciliation for this entry?")) return;

    try {
      setSaving(true);

      await client.post(
        `/finance/bank-reconciliation/unreconcile/${journalDetailId}`,
      );

      await loadEntries();
    } catch (e) {
      console.error(e);
      alert(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to undo reconciliation",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>
            <i className="bi bi-check2-square me-2"></i>
            Bank Reconciliation
          </div>
          <div style={styles.sub}>
            Match bank transactions with ERP ledger entries
          </div>
        </div>

        <div style={styles.body}>
          {error && (
            <div
              className="alert alert-danger py-2 mb-2"
              style={{ fontSize: 11 }}
            >
              {error}
            </div>
          )}

          <div className="row g-2 mb-2">
            <Field label="Bank Account" className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filter.accountId}
                onChange={(e) =>
                  setFilter({ ...filter, accountId: e.target.value })
                }
                disabled={loadingAccounts}
              >
                <option value="">
                  {loadingAccounts ? "Loading banks..." : "Select Bank"}
                </option>
                {bankAccounts.map((a) => {
                  const id = getVal(a, "AccountId", "accountId", "Id", "id");
                  const code = getVal(a, "AccountCode", "accountCode");
                  const name = getVal(a, "AccountName", "accountName");

                  return (
                    <option key={id} value={id}>
                      {code ? `${code} - ${name}` : name}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="From" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.fromDate}
                onChange={(e) =>
                  setFilter({ ...filter, fromDate: e.target.value })
                }
              />
            </Field>

            <Field label="To" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.toDate}
                onChange={(e) =>
                  setFilter({ ...filter, toDate: e.target.value })
                }
              />
            </Field>

            <Field label="Status" className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
              >
                <option value="false">Unreconciled</option>
                <option value="true">Reconciled</option>
                <option value="all">All</option>
              </select>
            </Field>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={loadEntries}
                disabled={loadingRows}
              >
                <i className="bi bi-search me-1"></i>
                {loadingRows ? "Loading..." : "Load"}
              </button>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <Stat title="Entries" value={rows.length} color="#0d6efd" />
            <Stat title="Pending" value={totals.pending} color="#ffc107" />
            <Stat
              title="Reconciled"
              value={totals.reconciled}
              color="#0f766e"
            />
            <Stat
              title="Debit"
              value={`₹ ${money(totals.debit)}`}
              color="#0f766e"
            />
            <Stat
              title="Credit"
              value={`₹ ${money(totals.credit)}`}
              color="#dc3545"
            />
            <Stat
              title="Net"
              value={`₹ ${money(Math.abs(totals.net))} ${
                totals.net >= 0 ? "Dr" : "Cr"
              }`}
              color={totals.net >= 0 ? "#0f766e" : "#dc3545"}
            />
          </div>

          <div
            style={{
              fontSize: 10,
              color: "#6c757d",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              Entries: <strong>{rows.length}</strong>
            </span>
            <span>
              Company ID: <strong>{companyId || "All"}</strong>
            </span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Voucher No",
                    "Description",
                    "Debit",
                    "Credit",
                    "Amount",
                    "Status",
                    "Bank Ref",
                    "Reconciled Date",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Debit", "Credit", "Amount"].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      {loadingRows ? "Loading entries..." : "No entries"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const isRec = Boolean(
                      getVal(r, "IsReconciled", "isReconciled"),
                    );
                    const entryDate = getVal(r, "EntryDate", "entryDate");
                    const recDate = getVal(
                      r,
                      "ReconciledDate",
                      "reconciledDate",
                    );

                    const debit = num(getVal(r, "Debit", "debit"));
                    const credit = num(getVal(r, "Credit", "credit"));
                    const amount =
                      num(getVal(r, "Amount", "amount")) ||
                      (debit > 0 ? debit : credit);

                    return (
                      <tr key={i}>
                        <td style={styles.td}>
                          {entryDate
                            ? new Date(entryDate).toLocaleDateString("en-IN")
                            : ""}
                        </td>

                        <td style={{ ...styles.td, fontWeight: 700 }}>
                          {getVal(r, "VoucherNo", "voucherNo")}
                        </td>

                        <td style={styles.td}>
                          {getVal(r, "Description", "description")}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {debit > 0 ? money(debit) : ""}
                        </td>

                        <td className="text-end" style={styles.td}>
                          {credit > 0 ? money(credit) : ""}
                        </td>

                        <td className="text-end fw-bold" style={styles.td}>
                          {money(amount)}
                        </td>

                        <td style={styles.td}>
                          <span
                            className={`badge ${
                              isRec ? "text-bg-success" : "text-bg-warning"
                            }`}
                            style={styles.badge}
                          >
                            {isRec ? "Reconciled" : "Pending"}
                          </span>
                        </td>

                        <td style={styles.td}>
                          {getVal(r, "BankReferenceNo", "bankReferenceNo") ||
                            "-"}
                        </td>

                        <td style={styles.td}>
                          {recDate
                            ? new Date(recDate).toLocaleDateString("en-IN")
                            : "-"}
                        </td>

                        <td style={styles.td}>
                          {isRec ? (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              style={styles.btn}
                              disabled={saving}
                              onClick={() => unreconcile(r)}
                            >
                              Undo
                            </button>
                          ) : (
                            <button
                              className="btn btn-success btn-sm"
                              style={styles.btn}
                              disabled={saving}
                              onClick={() => reconcile(r)}
                            >
                              Reconcile
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ title, value, color }) {
  return (
    <div className="col-md-2">
      <div style={{ ...styles.stat, borderTop: `3px solid ${color}` }}>
        <div style={{ fontSize: 10, color: "#6c757d", fontWeight: 700 }}>
          {title}
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color }}>{value}</div>
      </div>
    </div>
  );
}
